import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
	fetchEpstResultBySections,
	fetchEpstResultByCertificationId,
	createEpstResults,
	createEpstResultCertification
} from 'api/certifications/epst/results';
import { fetchUsers } from 'api/users/users';
import { fetchColleges } from 'api/colleges/colleges';
import {
	generateEpstFinish,
	updateCertificationCompletedById,
	updateCertificationStatusById
} from 'api/certifications/certifications';
import { handleApiError } from 'utils';

/**
 * Hook que gestiona toda la lógica de resultados al finalizar el examen EPST.
 * Encargado de:
 * - Obtener los puntajes por sección.
 * - Registrar los resultados si no existen.
 * - Asociar los resultados al usuario y colegio.
 * - Generar el documento final de certificación.
 * - Actualizar el estado y la fecha de finalización de la certificación.
 *
 * @param {Object} params
 * @param {Object} params.certification - Certificación actual con ID y college_id.
 * @param {string} params.token - Token de autenticación del usuario.
 * @param {string} params.username - Username del usuario autenticado.
 * @param {Function} params.onFinished - Callback que se ejecuta al finalizar todo el proceso.
 *
 * @returns {{ epstScores: Array }} Lista de resultados por sección.
 */
const useEpstSuccess = ({ certification, token, username, onFinished }) => {
	const [epstScores, setEpstScores] = useState([]);

	useEffect(() => {
		if (token) loadEpstScores();
	}, [token]);

	/**
	 * Carga los puntajes por sección del EPST.
	 * Si la certificación aún no tiene resultado final, lo genera.
	 */
	const loadEpstScores = async () => {
		try {
			const { data } = await fetchEpstResultBySections(certification.id, token);
			if (data?.status === 200) {
				setEpstScores(data.message);
				handleEpstResultByCertificationId(data.message);
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Verifica si ya existe el resultado final.
	 * Si no, crea los resultados por sección y el resultado total.
	 *
	 * @param {Array} scores - Puntajes por sección.
	 */
	const handleEpstResultByCertificationId = async (scores) => {
		try {
			const { data } = await fetchEpstResultByCertificationId(certification.id, token);
			if (data?.status === 200) return;
		} catch (error) {
			if (error.response?.status === 404) {
				let total = 0;
				for (let i = 0; i < scores.length; i++) {
					total += scores[i].total_points >= 0 ? scores[i].total_points : 0;
					await createEpstResults({
						certification_id: certification.id,
						section_id: i + 1,
						score: scores[i].total_points
					}, token);
				}
				await handleCreateEpstResultCertification({
					certification_id: certification.id,
					total_score: total,
					total_percentage: Math.round((total / 353) * 10000) / 100
				});
			} else {
				handleApiError(error);
			}
		}
	};

	/**
	 * Crea el resultado total de la certificación EPST.
	 *
	 * @param {Object} payload - Datos del resultado total.
	 */
	const handleCreateEpstResultCertification = async (payload) => {
		try {
			const { data } = await createEpstResultCertification(payload, token);
			if (data?.status === 201) handleUserByUsername();
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Consulta los datos del usuario por correo y continúa con la generación del certificado.
	 */
	const handleUserByUsername = async () => {
		try {
			const { data } = await fetchUsers({ username: encodeURIComponent(username) }, token);
			handleEpstSuccess(data);
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Obtiene la información del colegio del usuario y genera el documento de finalización.
	 *
	 * @param {Object} user - Datos del usuario autenticado.
	 */
	const handleEpstSuccess = async (user) => {
		try {
			const { data } = await fetchColleges({ id: certification.college_id }, token);
			const currentDate = moment().tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');

			await handleEpstGenerateFinish({
				name: `${user.name} ${user.lastname}`,
				date: currentDate,
				email: user.email,
				college: data.name
			});
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Genera el certificado final del EPST.
	 *
	 * @param {Object} data - Datos del documento de certificación.
	 */
	const handleEpstGenerateFinish = async (data) => {
		try {
			const response = await generateEpstFinish(data, token);
			if (response.data.status === 200) await handleUpdateCertificationStatusById();
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Actualiza el estado y fecha de finalización de la certificación a "completado".
	 */
	const handleUpdateCertificationStatusById = async () => {
		try {
			await updateCertificationStatusById(certification.id, { status: 2 }, token);
			const currentDate = moment().tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');
			await updateCertificationCompletedById(certification.id, { completed_date: currentDate }, token);
		} catch (error) {
			handleApiError(error);
		}
	};

	return {
		epstScores
	};
};

export default useEpstSuccess;